import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "../../services/api";
import { ujmsOpenIncidentPdf } from "../../services/ujmsApi";
import { inputCls, selectCls, btnGhost, cls, Badge } from "../ui";
import { Switch } from "../../components/ui/switch";
import {
  CircleCheck, CircleAlert, Circle, CloudUpload, Pencil, Printer, ArrowLeft, Send,
} from "lucide-react";

const U = "/ujms";
const REQ = "ps-required w-full h-11 border border-slate-300 rounded-[3px] px-3 pt-4 pb-1 text-sm font-semibold text-slate-800 focus:outline-none focus:border-ujms-dark";

function FloatField({ label, required, children, testId }) {
  return (
    <div className="relative" data-testid={testId}>
      <span className={cls("absolute left-3 top-1 text-[10px] font-bold uppercase tracking-wide z-10",
        required ? "text-[#8a6d1a]" : "text-slate-500")}>{required && "* "}{label}</span>
      {children}
    </div>
  );
}

const FIELD_SECTION = { incident_type: "General", location: "General", occurred_at: "General",
  narrative: "Report Narratives", persons: "Subjects", vehicles: "Vehicles", property_items: "Property" };

const SECTIONS = ["General", "Subjects", "Property", "Vehicles", "Contributing Officers", "Report Narratives"];

function sectionStatus(doc, name) {
  const errs = doc.ibr_errors || [];
  const errFields = { General: ["incident_type", "occurred_at", "location"], Subjects: ["persons"],
    "Report Narratives": ["narrative"] }[name] || [];
  if (errs.some((e) => errFields.includes(e.field))) return "attention";
  switch (name) {
    case "General": return doc.location && doc.incident_type ? "complete" : "started";
    case "Subjects": return (doc.persons || []).length ? "complete" : "empty";
    case "Property": return (doc.property_items || []).length ? "complete" : "empty";
    case "Vehicles": return (doc.vehicles || []).length ? "complete" : "empty";
    case "Contributing Officers": return (doc.contributing_officers || []).length ? "complete" : "empty";
    case "Report Narratives": return (doc.narrative || "").length >= 20 ? "complete" : "empty";
    default: return "empty";
  }
}

const STATUS_ICON = {
  complete: <CircleCheck className="w-4.5 h-4.5 w-5 h-5 text-emerald-600" />,
  attention: <CircleAlert className="w-5 h-5 text-orange-500" />,
  started: <Circle className="w-5 h-5 text-slate-300" />,
  empty: <Circle className="w-5 h-5 text-slate-300" />,
};
const STATUS_LABEL = { complete: "Complete", attention: "Needs attention", started: "In progress", empty: "Not started" };

export default function ReportWriting() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [section, setSection] = useState("General");
  const [saved, setSaved] = useState(true);
  const timer = useRef(null);

  const load = useCallback(() => {
    api.get(`${U}/le/incidents/${id}`).then((r) => setDoc(r.data)).catch(() => toast.error("Incident not found"));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const patch = (upd) => {
    setDoc((d) => ({ ...d, ...upd }));
    setSaved(false);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const r = await api.patch(`${U}/le/incidents/${id}`, upd);
        setDoc(r.data);
        setSaved(true);
      } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    }, 700);
  };

  if (!doc) return <div className="text-sm text-slate-400 p-6">Loading report…</div>;
  const locked = doc.status === "APPROVED";
  const sameFrom = (doc.occurred_at || "") === (doc.reported_at || doc.created_at || "").slice(0, 10);
  const sameTo = (doc.occurred_to || "") === (doc.reported_at || doc.created_at || "").slice(0, 10);
  const reportedDate = (doc.reported_at || doc.created_at || "").slice(0, 10);

  return (
    <div data-testid="report-writing-page">
      <div className="-mx-4 -mt-4 mb-4 bg-ujms-dark text-white px-4 py-2.5 flex items-center gap-3" data-testid="rw-title-bar">
        <span className="text-[15px] font-bold tracking-wide">Report Writing</span>
        <span className="font-mono text-xs opacity-80">{doc.incident_number}</span>
      </div>
      {/* header strip */}
      <div className="flex items-center gap-4 flex-wrap border-b border-[#d5dae8] pb-3 mb-4">
        <button className={btnGhost} onClick={() => navigate(`/officer/incident/${id}`)} data-testid="rw-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Case View
        </button>
        {[["Reported Date", reportedDate], ["Agency", "County Sheriff's Office"],
          ["Incident Type", doc.incident_type], ["Incident Number", doc.incident_number]].map(([l, v]) => (
          <div key={l}>
            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{l}</div>
            <div className="text-sm font-bold text-slate-800">{v}</div>
          </div>
        ))}
        <Badge value={doc.status} />
        <div className="ml-auto flex items-center gap-3">
          <span className={cls("inline-flex items-center gap-1.5 text-sm font-bold", saved ? "text-emerald-700" : "text-slate-400")} data-testid="rw-saved">
            <CloudUpload className="w-4.5 h-4.5 w-5 h-5" /> {saved ? "Saved" : "Saving…"}
          </span>
          <label className="inline-flex items-center gap-2 text-sm font-bold text-slate-600">
            Complete
            <Switch checked={["SUBMITTED", "APPROVED"].includes(doc.status)} disabled={locked}
              onCheckedChange={async (v) => {
                if (!v) return;
                try {
                  await api.post(`${U}/le/incidents/${id}/submit`);
                  toast.success("Report submitted for review");
                  load();
                } catch (e) {
                  const d = e.response?.data?.detail;
                  if (d?.ibr_errors) d.ibr_errors.forEach((er) => toast.error(`Edit ${er.edit_number} — ${er.message}`));
                  else toast.error(typeof d === "string" ? d : "Submit failed");
                }
              }} data-testid="rw-complete-toggle" />
          </label>
          <button className="p-1.5 text-slate-500 hover:text-ujms-dark" onClick={() => ujmsOpenIncidentPdf(id, doc.incident_number)} aria-label="Print" data-testid="rw-print">
            <Printer className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="flex gap-5 items-start">
        {/* left section rail */}
        <aside className="w-72 shrink-0 border border-[#d5dae8] rounded-sm" data-testid="rw-rail">
          {SECTIONS.map((s) => {
            const st = sectionStatus(doc, s);
            return (
              <button key={s} onClick={() => setSection(s)} data-testid={`rw-section-${s.toLowerCase().replace(/ /g, "-")}`}
                className={cls("w-full flex items-center gap-3 px-4 py-3 border-b border-[#e4e7f2] last:border-0 text-left transition-colors",
                  section === s ? "bg-[#eef0fa] border-l-4 border-l-ujms-dark" : "hover:bg-slate-50 border-l-4 border-l-transparent")}>
                {STATUS_ICON[st]}
                <span className="flex-1">
                  <span className="block text-sm font-bold text-slate-800">{s}</span>
                  <span className={cls("block text-[11px] font-semibold", st === "attention" ? "text-orange-600" : "text-slate-400")}>{STATUS_LABEL[st]}</span>
                </span>
              </button>
            );
          })}
        </aside>

        {/* main pane */}
        <div className="flex-1 min-w-0" data-testid="rw-pane">
          {locked && <div className="mb-3 text-sm font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2">This report is APPROVED and locked.</div>}

          {section === "General" && (
            <div className="space-y-4 max-w-2xl">
              <div className="border border-[#d5dae8] rounded-sm p-4 flex items-start justify-between" data-testid="rw-occurred-location">
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Occurred Location</div>
                  <div className="text-[15px] font-bold text-slate-800 mt-1">{doc.location || "— not set —"}</div>
                </div>
                <button className="p-1.5 text-ujms-dark" disabled={locked} aria-label="Edit location" data-testid="rw-location-edit"
                  onClick={() => { const v = window.prompt("Occurred location:", doc.location || ""); if (v !== null) patch({ location: v }); }}>
                  <Pencil className="w-4.5 h-4.5 w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FloatField label="Occurred Incident Type" required testId="rw-type-field">
                  <select className={REQ} value={doc.incident_type} disabled={locked}
                    onChange={(e) => patch({ incident_type: e.target.value })} data-testid="rw-type-select">
                    {["ASSAULT", "BURGLARY", "THEFT", "ROBBERY", "VANDALISM", "NARCOTICS", "DUI", "DOMESTIC", "WEAPONS", "TRAFFIC", "OTHER"].map((t) => <option key={t}>{t}</option>)}
                  </select>
                </FloatField>
                <FloatField label="Status" required testId="rw-status-field">
                  <select className={REQ} value={doc.report_status || "Open"} disabled={locked}
                    onChange={(e) => patch({ report_status: e.target.value })} data-testid="rw-status-select">
                    {["Open", "Closed", "Inactive"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </FloatField>
              </div>

              <div className="grid grid-cols-2 gap-4 items-center">
                <div className="grid grid-cols-2 gap-2">
                  <FloatField label="Occurred From Date" required testId="rw-from-field">
                    <input type="date" className={REQ} value={(doc.occurred_at || "").slice(0, 10)} disabled={locked}
                      onChange={(e) => patch({ occurred_at: e.target.value })} data-testid="rw-from-date" />
                  </FloatField>
                  <FloatField label="Occurred From Time" required testId="rw-from-time-field">
                    <input type="time" className={REQ} value={doc.occurred_time || ""} disabled={locked}
                      onChange={(e) => patch({ occurred_time: e.target.value })} data-testid="rw-from-time" />
                  </FloatField>
                </div>
                <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600">
                  <Switch checked={sameFrom} disabled={locked}
                    onCheckedChange={(v) => v && patch({ occurred_at: reportedDate })} data-testid="rw-same-from" />
                  Same as Reported Date/Time
                </label>
              </div>
              <div className="grid grid-cols-2 gap-4 items-center">
                <div className="grid grid-cols-2 gap-2">
                  <FloatField label="Occurred To Date" testId="rw-to-field">
                    <input type="date" className={`${REQ} bg-white`} style={{ background: "white" }} value={(doc.occurred_to || "").slice(0, 10)} disabled={locked}
                      onChange={(e) => patch({ occurred_to: e.target.value })} data-testid="rw-to-date" />
                  </FloatField>
                  <FloatField label="Occurred To Time" testId="rw-to-time-field">
                    <input type="time" className={`${REQ} bg-white`} style={{ background: "white" }} value={doc.occurred_to_time || ""} disabled={locked}
                      onChange={(e) => patch({ occurred_to_time: e.target.value })} data-testid="rw-to-time" />
                  </FloatField>
                </div>
                <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600">
                  <Switch checked={sameTo} disabled={locked}
                    onCheckedChange={(v) => v && patch({ occurred_to: reportedDate })} data-testid="rw-same-to" />
                  Same as Reported Date/Time
                </label>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FloatField label="Status Date" required testId="rw-status-date-field">
                  <input type="date" className={REQ} value={(doc.status_date || reportedDate || "").slice(0, 10)} disabled={locked}
                    onChange={(e) => patch({ status_date: e.target.value })} data-testid="rw-status-date" />
                </FloatField>
              </div>
            </div>
          )}

          {section === "Subjects" && (
            <ListEditor locked={locked} testPrefix="rw-subject"
              rows={(doc.persons || []).map((p) => `${p.name} — ${p.role}`)}
              fields={[{ key: "name", placeholder: "Full name" }, { key: "role", type: "select", options: ["SUSPECT", "VICTIM", "WITNESS"] }]}
              onAdd={(v) => patch({ persons: [...(doc.persons || []), { name: v.name, role: v.role || "SUSPECT" }] })}
              onRemove={(i) => patch({ persons: (doc.persons || []).filter((_, x) => x !== i) })} />
          )}
          {section === "Property" && (
            <ListEditor locked={locked} testPrefix="rw-property"
              rows={doc.property_items || []}
              fields={[{ key: "item", placeholder: "Property / evidence description" }]}
              onAdd={(v) => patch({ property_items: [...(doc.property_items || []), v.item] })}
              onRemove={(i) => patch({ property_items: (doc.property_items || []).filter((_, x) => x !== i) })} />
          )}
          {section === "Vehicles" && (
            <ListEditor locked={locked} testPrefix="rw-vehicle"
              rows={(doc.vehicles || []).map((v) => `${v.plate || "no plate"} — ${v.make_model || "unknown"}`)}
              fields={[{ key: "plate", placeholder: "Plate" }, { key: "make_model", placeholder: "Make / model" }]}
              onAdd={(v) => patch({ vehicles: [...(doc.vehicles || []), { plate: (v.plate || "").toUpperCase(), make_model: v.make_model || "" }] })}
              onRemove={(i) => patch({ vehicles: (doc.vehicles || []).filter((_, x) => x !== i) })} />
          )}
          {section === "Contributing Officers" && (
            <ListEditor locked={locked} testPrefix="rw-officer"
              rows={doc.contributing_officers || []}
              fields={[{ key: "name", placeholder: "Officer name / star number" }]}
              onAdd={(v) => patch({ contributing_officers: [...(doc.contributing_officers || []), v.name] })}
              onRemove={(i) => patch({ contributing_officers: (doc.contributing_officers || []).filter((_, x) => x !== i) })} />
          )}
          {section === "Report Narratives" && (
            <div className="max-w-2xl">
              <FloatField label="Narrative (minimum 20 characters)" required testId="rw-narrative-field">
                <textarea rows={10} className={`${REQ} h-auto pt-6 leading-relaxed`} value={doc.narrative || ""} disabled={locked}
                  onChange={(e) => patch({ narrative: e.target.value })} data-testid="rw-narrative" />
              </FloatField>
            </div>
          )}

          {!locked && ["DRAFT", "REJECTED"].includes(doc.status) && !(doc.ibr_errors || []).length && (
            <button className="mt-6 inline-flex items-center gap-1.5 bg-ujms-dark text-white text-sm font-bold px-4 py-2 rounded-sm hover:bg-ujms"
              onClick={async () => {
                try { await api.post(`${U}/le/incidents/${id}/submit`); toast.success("Report submitted for review"); load(); }
                catch (e) { toast.error(e.response?.data?.detail?.message || "Submit failed"); }
              }} data-testid="rw-submit-btn"><Send className="w-4 h-4" /> Submit for Review</button>
          )}
        </div>

        {/* IBR Case Edit List right rail */}
        {(doc.ibr_errors || []).length > 0 && (
          <aside className="w-80 shrink-0 border border-[#d5dae8] rounded-sm overflow-hidden" data-testid="rw-ibr-rail">
            <div className="bg-ujms-dark text-white text-sm font-bold px-3 py-2.5">IBR Case Edit List ({doc.ibr_errors.length})</div>
            <div className="divide-y divide-[#e4e7f2]">
              {doc.ibr_errors.map((e, i) => (
                <div key={`${e.edit_number}-${i}`} className="p-3 cursor-pointer hover:bg-indigo-50 transition-colors"
                  onClick={() => setSection(FIELD_SECTION[e.field] || "General")}
                  title={`Open the ${FIELD_SECTION[e.field] || "General"} section`} data-testid={`rw-ibr-card-${i}`}>
                  <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Edit Text</div>
                  <div className="text-[13px] font-semibold text-slate-800 mt-0.5 leading-snug">{e.message}</div>
                  <div className="flex gap-6 mt-2 items-end">
                    <span><span className="text-[10px] font-bold uppercase text-slate-400 block">Edit #</span><span className="text-xs font-bold text-slate-700">{e.edit_number}</span></span>
                    <span><span className="text-[10px] font-bold uppercase text-slate-400 block">Edit Type</span><span className="text-xs font-bold text-slate-700">{e.edit_type}</span></span>
                    <span className="ml-auto text-[11px] font-bold text-ujms-dark">Fix in {FIELD_SECTION[e.field] || "General"} →</span>
                  </div>
                </div>
              ))}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

function ListEditor({ rows, fields, onAdd, onRemove, locked, testPrefix }) {
  const [v, setV] = useState({});
  const first = fields[0].key;
  return (
    <div className="max-w-2xl space-y-3">
      {!locked && (
        <div className="flex gap-2 items-end">
          {fields.map((f) => f.type === "select" ? (
            <select key={f.key} className={`${selectCls} w-36`} value={v[f.key] || f.options[0]}
              onChange={(e) => setV({ ...v, [f.key]: e.target.value })} data-testid={`${testPrefix}-${f.key}`}>
              {f.options.map((o) => <option key={o}>{o}</option>)}
            </select>
          ) : (
            <input key={f.key} className={`${inputCls} flex-1`} placeholder={f.placeholder} value={v[f.key] || ""}
              onChange={(e) => setV({ ...v, [f.key]: e.target.value })} data-testid={`${testPrefix}-${f.key}`} />
          ))}
          <button className="bg-ujms-dark text-white text-sm font-bold px-4 py-1.5 rounded-sm hover:bg-ujms disabled:opacity-50"
            disabled={!(v[first] || "").trim()} onClick={() => { onAdd(v); setV({}); }} data-testid={`${testPrefix}-add`}>Add</button>
        </div>
      )}
      <div className="border border-[#d5dae8] rounded-sm divide-y divide-[#e4e7f2]">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-slate-700" data-testid={`${testPrefix}-row-${i}`}>
            <span className="flex-1">{r}</span>
            {!locked && <button className="text-xs font-bold text-red-600 underline" onClick={() => onRemove(i)} data-testid={`${testPrefix}-del-${i}`}>remove</button>}
          </div>
        ))}
        {!rows.length && <div className="px-3 py-3 text-sm text-slate-400">Nothing entered yet.</div>}
      </div>
    </div>
  );
}
